import {ApolloProvider} from '@apollo/client';
import {
  MyRewardsTheme,
  ToastStateProvider,
  WidgetProvider,
} from '@eCommerce/frontend-legacy-shared-library-native';
import envData from 'env.json';
import {ITheme, NativeBaseProvider, NativeBaseProviderProps} from 'native-base';
import React, {useCallback, useMemo} from 'react';
import {View} from 'react-native';
import {Platform} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {
  Provider,
  createDispatchHook,
  createSelectorHook,
  createStoreHook,
} from 'react-redux';
import {PersistGate} from 'redux-persist/integration/react';
import 'resize-observer-polyfill/dist/ResizeObserver.global';

import {client} from 'services/graphql';
import {AuthActions, LogoutMsg} from 'store/auth/auth.reducer';
import {ContentActions} from 'store/content';
import {MarketsActions} from 'store/markets/markets.reducer';
import {PropertiesActions} from 'store/properties';
import {getLogin} from 'utils/navigation';

import {FCC} from '../types';
import ConnectedThemeProvider from './containers/ConnectedThemeProvider';
import styles from './index.stylesheet';
import {persistor, store} from './store';
import {ModalProvider, ModalService} from './utils/modal';
import {getForwardUrl} from 'navigation';

const {API_URL, USE_STORYBOOK} = envData;

interface ProviderProps {
  disableStartupItems?: boolean;
}

const startupItems = () => {
  store.dispatch(ContentActions.fetchNavigation());
  store.dispatch(MarketsActions.getMarkets());
  store.dispatch(PropertiesActions.fetch());
  if (Platform.OS === 'web') {
    store.dispatch(ContentActions.fetchEmpireFooterContent());
    store.dispatch(ContentActions.checkHeaderFooterSuppression());
  }
};
export const AppContext = React.createContext<any>(null);

export const useStore = createStoreHook(AppContext);
export const useDispatch = createDispatchHook(AppContext);
export const useSelector = createSelectorHook(AppContext);

const ProviderWrapper: FCC<ProviderProps> = ({
  children,
  disableStartupItems,
}) => {
  const persisGateProps = useMemo(() => {
    return USE_STORYBOOK || disableStartupItems
      ? {
          loading: null,
          persistor: persistor,
        }
      : {
          loading: null,
          persistor: persistor,
          onBeforeLift: startupItems,
        };
  }, [disableStartupItems]);

  const handleUserChange = useCallback(() => {
    store.dispatch(AuthActions.changeUser());
  }, []);

  const handleLogout = useCallback(() => {
    store.dispatch(
      AuthActions.logout({
        msg: LogoutMsg.LOGOUT,
      })
    );
  }, []);

  const handleTimeout = useCallback(() => {
    store.dispatch(
      AuthActions.logout({
        msg: LogoutMsg.TIMEOUT,
        forwardUrl: getForwardUrl(),
      })
    );
  }, [window.location.pathname]);

  return (
    <ApolloProvider client={client}>
      <Provider context={AppContext} store={store}>
        <PersistGate {...persisGateProps}>
          <WidgetProvider
            disableStartupItems={disableStartupItems}
            onLogout={handleLogout}
            onTimeout={handleTimeout}
            onLogin={getLogin}
            onUserChange={handleUserChange}
            isLegacyMyRewards={window.isLegacyMyRewards}
            baseUrl={API_URL}>
            <NativeBaseProvider theme={MyRewardsTheme as unknown as ITheme}>
              <ConnectedThemeProvider>
                <ToastStateProvider>
                  <SafeAreaProvider>
                    <View style={styles.applicationView}>
                      <ModalProvider value={ModalService}>
                        {children}
                      </ModalProvider>
                    </View>
                  </SafeAreaProvider>
                </ToastStateProvider>
              </ConnectedThemeProvider>
            </NativeBaseProvider>
          </WidgetProvider>
        </PersistGate>
      </Provider>
    </ApolloProvider>
  );
};

export default ProviderWrapper;
